import { useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { getTodayHolidays, type HolidayCountry } from "./holidayData";

const STORAGE_PREFIX = "holiday-dismissed-";

const BG_COLORS: Record<HolidayCountry, string> = {
  TR: "#D32F2F",
  US: "#002868",
  BOTH: "#6A1B9A",
};

const FLAG_SRCS: Record<HolidayCountry, string[]> = {
  TR: ["/icons/tr.png"],
  US: ["/icons/us.png"],
  BOTH: ["/icons/tr.png", "/icons/us.png"],
};

function getDismissKey(date: Date, holidayKey: string): string {
  return `${STORAGE_PREFIX}${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}-${holidayKey}`;
}

const FlagImg = ({ src, alt }: { src: string; alt: string }) => (
  <Box
    component="img"
    src={`${src}?width=40`}
    alt={alt}
    sx={{
      width: 20,
      height: 14,
      objectFit: "cover",
      borderRadius: 0.25,
      flexShrink: 0,
    }}
  />
);

export const HolidayBanner = () => {
  const { t } = useTranslation();
  const today = new Date();
  const holidays = getTodayHolidays(today);

  const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(() => {
    const set = new Set<string>();
    holidays.forEach((h) => {
      const key = getDismissKey(today, h.key);
      if (localStorage.getItem(key) === "1") set.add(h.key);
    });
    return set;
  });

  const visible = holidays.filter((h) => !dismissedKeys.has(h.key));

  if (visible.length === 0) return null;

  const dismiss = (holidayKey: string) => {
    localStorage.setItem(getDismissKey(today, holidayKey), "1");
    setDismissedKeys((prev) => new Set(prev).add(holidayKey));
  };

  return (
    <>
      {visible.map((holiday) => (
        <Box
          key={holiday.key}
          sx={{
            backgroundColor: BG_COLORS[holiday.country],
            color: "#fff",
            px: 2,
            py: 0.75,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            position: "relative",
            minHeight: 36,
          }}
        >
          {FLAG_SRCS[holiday.country].map((src) => (
            <FlagImg key={src} src={src} alt={holiday.country} />
          ))}
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, textAlign: "center", lineHeight: 1.4 }}
          >
            {t(`holiday.${holiday.key}`)}
          </Typography>
          {FLAG_SRCS[holiday.country].map((src) => (
            <FlagImg key={src} src={src} alt={holiday.country} />
          ))}
          <IconButton
            size="small"
            aria-label="dismiss"
            onClick={() => dismiss(holiday.key)}
            sx={{
              position: "absolute",
              right: 8,
              color: "rgba(255,255,255,0.85)",
              "&:hover": { color: "#fff" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </>
  );
};
